/**
 * One list for every dropdown.
 *
 * A plain ``<select>`` opens the operating system's own list, which looks
 * foreign (black on macOS), cannot be themed and grows with the number of
 * options. This replaces that list, for every ``<select>`` on the page,
 * with one drawn here: a panel of fixed maximum height that scrolls, in the
 * app's own colours.
 *
 * It is installed once for the whole document rather than select by select,
 * so pages, dialogs, the labeling editor and extension pages all get it
 * without changing their markup. The ``<select>`` stays the real control:
 * its value, its change events, its label and its keyboard focus are
 * untouched; only the list that opens is ours.
 *
 * Left to the browser: ``multiple`` and ``size > 1`` selects (they are
 * already in-page lists), any select with ``data-native-list``, and devices
 * whose only pointer is touch, where the system picker is the better list.
 */

const POPUP_CLASS = "afri-select-list";
const MAX_HEIGHT = 280;
const GAP = 4;
const EDGE = 8;

// Every dropdown says it is one: a chevron at its right edge, whatever
// styles the page gives it (many reset ``appearance`` and lose the
// browser's own arrow). Lists that show several rows have no chevron.
const CHEVRON =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none' stroke='%236b7280' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M4 6l4 4 4-4'/%3E%3C/svg%3E\")";

const STYLE = `
select:not([multiple]):not([size]),
select[size="1"]:not([multiple]) {
  -webkit-appearance: none !important;
  appearance: none !important;
  background-image: ${CHEVRON} !important;
  background-repeat: no-repeat !important;
  background-position: right 8px center !important;
  background-size: 16px 16px !important;
  padding-right: 30px !important;
  /* No !important here: a page that sets its own padding keeps it.
     This is only for the ones that set none and read flush against
     their own edge. */
  padding-left: 10px;
  text-overflow: ellipsis;
}
select:not([multiple]):not([size]):disabled {
  opacity: 0.6;
}
.${POPUP_CLASS} {
  position: fixed;
  z-index: 2147483000;
  box-sizing: border-box;
  max-height: ${MAX_HEIGHT}px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 4px;
  border: 1px solid var(--color-neutral-border, #d9d9d4);
  border-radius: 8px;
  background: var(--color-neutral-background, #fff);
  color: var(--color-neutral-content, #1f1f1f);
  box-shadow: 0 8px 24px rgb(0 0 0 / 14%), 0 2px 6px rgb(0 0 0 / 8%);
  font-family: var(--font-family-body, inherit);
  font-size: 14px;
  line-height: 20px;
  scrollbar-width: thin;
}
.${POPUP_CLASS}__option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
}
.${POPUP_CLASS}__option > span {
  overflow: hidden;
  text-overflow: ellipsis;
}
.${POPUP_CLASS}__option[data-active="true"] {
  background: var(--color-neutral-surface-active, rgb(0 0 0 / 6%));
}
.${POPUP_CLASS}__option[aria-selected="true"] {
  color: var(--color-primary-content, #3b4ea8);
  font-weight: 500;
}
.${POPUP_CLASS}__option[aria-selected="true"]::after {
  content: "";
  flex: none;
  width: 5px;
  height: 10px;
  margin: 0 2px 3px auto;
  border: solid currentColor;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.${POPUP_CLASS}__option[aria-disabled="true"] {
  color: var(--color-neutral-content-subtlest, #9a9a94);
  cursor: default;
}
.${POPUP_CLASS}__group {
  padding: 8px 10px 4px;
  color: var(--color-neutral-content-subtler, #6f6f69);
  font-size: 12px;
  font-weight: 600;
}
.${POPUP_CLASS}__group ~ .${POPUP_CLASS}__option[data-grouped="true"] {
  padding-left: 18px;
}
`;

type Open = {
  select: HTMLSelectElement;
  panel: HTMLDivElement;
  items: { el: HTMLDivElement; option: HTMLOptionElement; disabled: boolean }[];
  active: number;
  typed: string;
  typedAt: number;
};

let open: Open | null = null;

const touchOnly = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: coarse)").matches &&
  !window.matchMedia("(any-pointer: fine)").matches;

const ours = (target: EventTarget | null): HTMLSelectElement | null => {
  const select = target instanceof Element ? target.closest("select") : null;
  if (!select || select.disabled || select.multiple || select.size > 1) return null;
  if (select.hasAttribute("data-native-list") || touchOnly()) return null;
  return select;
};

const place = (state: Open) => {
  const { select, panel } = state;
  const box = select.getBoundingClientRect();
  const width = Math.min(Math.max(box.width, 160), window.innerWidth - EDGE * 2);
  panel.style.minWidth = `${width}px`;
  panel.style.maxWidth = `${Math.min(Math.max(width, 420), window.innerWidth - EDGE * 2)}px`;
  const below = window.innerHeight - box.bottom - GAP - EDGE;
  const above = box.top - GAP - EDGE;
  // Below when the whole list fits there, or when there is more room below.
  const wanted = Math.min(panel.scrollHeight + 2, MAX_HEIGHT);
  const goUp = wanted > below && above > below;
  panel.style.maxHeight = `${Math.max(120, Math.min(MAX_HEIGHT, goUp ? above : below))}px`;
  const height = Math.min(panel.offsetHeight, goUp ? above : below);
  panel.style.top = `${goUp ? Math.max(EDGE, box.top - GAP - height) : box.bottom + GAP}px`;
  const left = Math.min(box.left, window.innerWidth - EDGE - panel.offsetWidth);
  panel.style.left = `${Math.max(EDGE, left)}px`;
};

const setActive = (state: Open, index: number, scroll = true) => {
  if (index < 0 || index >= state.items.length) return;
  state.items[state.active]?.el.removeAttribute("data-active");
  state.active = index;
  const { el } = state.items[index];
  el.setAttribute("data-active", "true");
  state.select.setAttribute("aria-activedescendant", el.id);
  if (scroll) el.scrollIntoView?.({ block: "nearest" });
};

const step = (state: Open, from: number, by: 1 | -1) => {
  for (let i = from + by; i >= 0 && i < state.items.length; i += by) {
    if (!state.items[i].disabled) return setActive(state, i);
  }
};

export const closeSelectList = () => {
  if (!open) return;
  const { select, panel } = open;
  open = null;
  panel.remove();
  select.removeAttribute("aria-activedescendant");
  select.removeAttribute("aria-expanded");
};

const choose = (state: Open, index: number) => {
  const { select } = state;
  const { option, disabled } = state.items[index];
  if (disabled) return;
  closeSelectList();
  if (option.index === select.selectedIndex) return;
  select.selectedIndex = option.index;
  // What the browser fires when its own list is used; React listens to these.
  select.dispatchEvent(new Event("input", { bubbles: true }));
  select.dispatchEvent(new Event("change", { bubbles: true }));
};

const openList = (select: HTMLSelectElement) => {
  closeSelectList();
  const panel = document.createElement("div");
  panel.className = POPUP_CLASS;
  panel.setAttribute("role", "listbox");
  const items: Open["items"] = [];
  const add = (option: HTMLOptionElement, group?: HTMLOptGroupElement) => {
    if (option.hidden) return;
    const disabled = option.disabled || !!group?.disabled;
    const el = document.createElement("div");
    el.className = `${POPUP_CLASS}__option`;
    el.id = `afri-select-option-${items.length}`;
    el.setAttribute("role", "option");
    el.setAttribute("aria-selected", String(option.index === select.selectedIndex));
    if (disabled) el.setAttribute("aria-disabled", "true");
    if (group) el.setAttribute("data-grouped", "true");
    const text = document.createElement("span");
    // An option with no text (a blank "none" choice) still needs a row.
    text.textContent = option.label || option.text || " ";
    el.title = option.label || option.text;
    el.appendChild(text);
    panel.appendChild(el);
    items.push({ el, option, disabled });
  };
  for (const child of Array.from(select.children)) {
    if (child instanceof HTMLOptGroupElement) {
      const label = document.createElement("div");
      label.className = `${POPUP_CLASS}__group`;
      label.textContent = child.label;
      panel.appendChild(label);
      for (const option of Array.from(child.children)) {
        if (option instanceof HTMLOptionElement) add(option, child);
      }
    } else if (child instanceof HTMLOptionElement) {
      add(child);
    }
  }
  if (!items.length) return;

  const state: Open = { select, panel, items, active: -1, typed: "", typedAt: 0 };
  // Pressing inside the list must not take focus from the select.
  panel.addEventListener("mousedown", (e) => e.preventDefault());
  panel.addEventListener("click", (e) => {
    const el = (e.target as Element).closest(`.${POPUP_CLASS}__option`);
    const index = items.findIndex((item) => item.el === el);
    if (index >= 0) choose(state, index);
  });
  panel.addEventListener("mousemove", (e) => {
    const el = (e.target as Element).closest(`.${POPUP_CLASS}__option`);
    const index = items.findIndex((item) => item.el === el);
    if (index >= 0 && index !== state.active && !items[index].disabled) setActive(state, index, false);
  });

  document.body.appendChild(panel);
  open = state;
  select.setAttribute("aria-expanded", "true");
  place(state);
  const current = items.findIndex((item) => item.option.index === select.selectedIndex);
  setActive(state, current >= 0 ? current : 0, false);
  // Show the current choice in the middle of the window, not at its edge.
  const el = items[state.active].el;
  panel.scrollTop = el.offsetTop - (panel.clientHeight - el.offsetHeight) / 2;
};

const onMouseDown = (e: MouseEvent) => {
  if (open && e.target instanceof Node && open.panel.contains(e.target)) return;
  const select = e.button === 0 ? ours(e.target) : null;
  if (!select) return closeSelectList();
  // Stops the browser's own list. Focus is given by hand, as it would have been.
  e.preventDefault();
  select.focus();
  if (open?.select === select) closeSelectList();
  else openList(select);
};

const typeAhead = (state: Open, key: string) => {
  const now = Date.now();
  state.typed = now - state.typedAt > 700 ? key : state.typed + key;
  state.typedAt = now;
  const wanted = state.typed.toLowerCase();
  // The same letter again moves on to the next match, as system lists do.
  const repeat = wanted.length > 1 && [...wanted].every((ch) => ch === wanted[0]);
  const start = repeat || wanted.length === 1 ? state.active + 1 : state.active;
  const prefix = repeat ? wanted[0] : wanted;
  for (let n = 0; n < state.items.length; n++) {
    const i = (start + n) % state.items.length;
    const { option, disabled } = state.items[i];
    if (!disabled && (option.label || option.text).trim().toLowerCase().startsWith(prefix)) {
      return setActive(state, i);
    }
  }
};

const onKeyDown = (e: KeyboardEvent) => {
  if (e.defaultPrevented) return;
  if (!open) {
    const select = ours(e.target);
    if (!select || e.target !== select || e.ctrlKey || e.metaKey) return;
    if (["ArrowDown", "ArrowUp", "Enter", " ", "F4"].includes(e.key)) {
      e.preventDefault();
      openList(select);
    }
    return;
  }
  const state = open;
  if (e.key === "Tab") return closeSelectList();
  if (e.ctrlKey || e.metaKey) return;
  const last = state.items.length;
  if (e.key === "Escape") closeSelectList();
  else if (e.key === "ArrowDown") step(state, state.active, 1);
  else if (e.key === "ArrowUp") step(state, state.active, -1);
  else if (e.key === "Home") step(state, -1, 1);
  else if (e.key === "End") step(state, last, -1);
  else if (e.key === "PageDown") step(state, Math.min(last - 1, state.active + 7) - 1, 1);
  else if (e.key === "PageUp") step(state, Math.max(0, state.active - 7) + 1, -1);
  else if (e.key === "Enter" || (e.key === " " && !state.typed)) choose(state, state.active);
  else if (e.key.length === 1) typeAhead(state, e.key);
  else return;
  e.preventDefault();
  // A dialog must not also act on the key (Escape would close it too).
  // Dialogs listen on the document as well, so stopping at later targets
  // is not enough.
  e.stopImmediatePropagation();
};

let installed = false;

/** Install once, for the whole document. Safe to call again. */
export const installSelectPopup = () => {
  if (installed || typeof document === "undefined") return;
  installed = true;
  const style = document.createElement("style");
  style.setAttribute("data-afri-select-list", "");
  style.textContent = STYLE;
  document.head.appendChild(style);
  // Capture, on the window: ahead of dialogs and editors, which listen on
  // the document and may stop these events or act on them first.
  window.addEventListener("mousedown", onMouseDown, true);
  window.addEventListener("keydown", onKeyDown, true);
  // The select moved or went away: the list no longer belongs anywhere.
  document.addEventListener(
    "scroll",
    (e) => {
      if (!open || (e.target instanceof Node && open.panel.contains(e.target))) return;
      if (!open.select.isConnected) return closeSelectList();
      place(open);
    },
    true,
  );
  window.addEventListener("resize", closeSelectList);
  window.addEventListener("blur", closeSelectList);
  document.addEventListener("focusin", (e) => {
    if (open && e.target !== open.select) closeSelectList();
  });
};
